import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { usePortalStore } from '../../../store/portalStore';
import { portalApi } from './../portalApi';
import type { ConversationSummary } from './../portalApi';
import { t } from '../../../../../widget/i18n';
import styles from './PortalSidebar.module.css';

export interface PortalSidebarUser {
  email: string;
  displayName?: string;
}

interface PortalSidebarProps {
  portalTitle: string;
  user: PortalSidebarUser | null;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  onLogout: () => void;
}

export default function PortalSidebar({ portalTitle, user, onSelectConversation, onNewChat, onLogout }: PortalSidebarProps) {
  const {
    conversations,
    activeConversationId,
    pendingTurns,
    loading,
    loadingMore,
    hasMore,
    removeConversation,
    fetchMoreConversations,
    setSidebarOpen,
  } = usePortalStore();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const grouped = groupByDate(conversations);

  const pendingEntries = Object.entries(pendingTurns);
  const newPendingEntries = pendingEntries.filter(([, t]) => t.conversationId === null);
  const pendingStatusByConversationId = new Map(
    pendingEntries.filter(([, t]) => t.conversationId !== null).map(([turnId, t]) => [t.conversationId as string, { turnId, status: t.status }]),
  );

  // Infinite scroll - IntersectionObserver on sentinel element
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchMoreConversations();
    }
  }, [loadingMore, hasMore, fetchMoreConversations]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      { rootMargin: '100px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  async function handleDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await portalApi.deleteConversation(id);
      removeConversation(id);
    } catch (err) {
      console.warn('[portal] Failed to delete conversation:', err);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <aside className={styles.sidebar}>
      {/* Portal branding / title */}
      <div className={styles.branding}>
        <div className={styles.brandingIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
          </svg>
        </div>
        <span className={styles.brandingTitle}>{portalTitle || '\u00A0'}</span>
        <button
          className={styles.closeBtn}
          onClick={() => setSidebarOpen(false)}
          title={t('portalHideMenu')}
          aria-label={t('portalHideMenu')}
        >
          <X size={18} />
        </button>
      </div>

      {/* New chat button */}
      <div className={styles.header}>
        <button className={styles.newChatBtn} onClick={onNewChat}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('portalNewChat')}
        </button>
      </div>

      <div className={styles.conversationList}>
        {loading && conversations.length === 0 && newPendingEntries.length === 0 && (
          <div className={styles.loadingState}>
            <span className={styles.spinner} />
            {t('portalLoading')}
          </div>
        )}

        {!loading && conversations.length === 0 && newPendingEntries.length === 0 && (
          <div className={styles.emptyState}>{t('portalNoConversations')}</div>
        )}

        {newPendingEntries.length > 0 && (
          <div className={styles.group}>
            {newPendingEntries.map(([turnId, turn]) => (
              <button
                key={turnId}
                className={`${styles.conversationItem} ${activeConversationId === turnId ? styles.active : ''}`}
                onClick={() => onSelectConversation(turnId)}
              >
                <span className={styles.conversationTitle}>{turn.title}</span>
                {turn.status === 'sending' ? (
                  <span className={styles.spinner} />
                ) : (
                  <span className={styles.warningIcon} title={turn.status === 'timeout' ? t('portalTurnTimedOut') : t('portalTurnError')}>!</span>
                )}
              </button>
            ))}
          </div>
        )}

        {grouped.map(group => (
          <div key={group.label} className={styles.group}>
            <div className={styles.groupLabel}>{group.label}</div>
            {group.items.map(conv => {
              const pending = pendingStatusByConversationId.get(conv.id);
              const isDeleting = deletingId === conv.id;
              const selectConv = () => {
                if (isDeleting) return;
                onSelectConversation(pending ? pending.turnId : conv.id);
              };
              return (
                <div
                  key={conv.id}
                  role="button"
                  tabIndex={0}
                  aria-disabled={isDeleting}
                  className={`${styles.conversationItem} ${activeConversationId === conv.id ? styles.active : ''}`}
                  onClick={selectConv}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectConv(); } }}
                >
                  <span className={styles.conversationTitle}>
                    {conv.title || t('portalUntitledConversation')}
                  </span>
                  {pending?.status === 'sending' && <span className={styles.spinner} />}
                  {pending && pending.status !== 'sending' && (
                    <span className={styles.warningIcon} title={pending.status === 'timeout' ? t('portalTurnTimedOut') : t('portalTurnError')}>!</span>
                  )}
                  {!pending && (
                    <button
                      className={styles.deleteBtn}
                      onClick={(e) => handleDelete(e, conv.id)}
                      title={t('portalDeleteConversation')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {/* Lazy load sentinel + loading indicator */}
        {hasMore && (
          <div ref={sentinelRef} className={styles.loadMoreSentinel}>
            {loadingMore && (
              <div className={styles.loadingMore}>
                <span className={styles.spinner} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* User section at the bottom */}
      <div className={styles.userSection}>
        <div className={styles.userAvatar}>
          {user
            ? (user.displayName || user.email).charAt(0).toUpperCase()
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
          }
        </div>
        <span className={styles.userEmail}>
          {user
            ? (user.displayName || user.email)
            : t('portalGuest')}
        </span>
        {user && (
          <button className={styles.logoutBtn} onClick={onLogout} title={t('portalLogout')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        )}
      </div>
    </aside>
  );
}

interface DateGroup {
  label: string;
  items: ConversationSummary[];
}

function groupByDate(conversations: ConversationSummary[]): DateGroup[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups: Record<'today' | 'yesterday' | 'last7Days' | 'older', ConversationSummary[]> = {
    today: [],
    yesterday: [],
    last7Days: [],
    older: [],
  };

  for (const conv of conversations) {
    const date = new Date(conv.updatedAt || conv.createdAt);
    if (date >= today) {
      groups.today.push(conv);
    } else if (date >= yesterday) {
      groups.yesterday.push(conv);
    } else if (date >= weekAgo) {
      groups.last7Days.push(conv);
    } else {
      groups.older.push(conv);
    }
  }

  const labelKeys = {
    today: 'today',
    yesterday: 'yesterday',
    last7Days: 'portalLast7Days',
    older: 'portalOlder',
  } as const;

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([key, items]) => ({ label: t(labelKeys[key as keyof typeof labelKeys]), items }));
}
